import { BfxBlotterOrdersContextMenuActionPayload } from "./BfxBlotterOrdersContextMenuActionPayload";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

export class BfxBlotterOrdersContextMenuAcceptanceState {
  public actionRequests: BfxBlotterOrdersContextMenuActionPayload = {
    accept: {
      acceptance: new Map<CubiqTS.AcceptanceType, string[]>(),
      xfer: new Map<string, string[]>(),
    },
    acceptInto: {
      acceptance: new Map<CubiqTS.AcceptanceType, string[]>(),
      xfer: new Map<string, string[]>(),
    },
    reject: {
      acceptance: new Map<CubiqTS.AcceptanceType, string[]>(),
      xfer: new Map<string, string[]>(),
    },
  };

  public warnings = {
    show: false,
    warning_count: 0,
    external: {
      display: false,
      ids: [],
      orderRefs: [],
    },
  };

  public processedItemIndex: number = -1;
  public processedSiblingStrategyIds = new Set<string>();

  // Note: specifically excluded from count mechanism
  public acceptIntoElegible: number = 0;

  public ineligibleItem: boolean = false;
  public ineligibleAccept: boolean = false;

  constructor(
    public count: number = 0,
    /**
     * call orders without a RateSideType
     */
    public callCount: number = 0,
    public cancelCount: number = 0,
    public expiryCount: number = 0,
    public migrationCount: number = 0,
    public modifyInCount: number = 0,
    public modifyOutCount: number = 0,
    public newPendingCount: number = 0,
    public transferCount: number = 0,
    public pauseCount: number = 0,
    public resumeCount: number = 0,
  ) {}

  public addAcceptRequest(type: CubiqTS.AcceptanceType, id: string) {
    if (!this.actionRequests.accept.acceptance.has(type)) {
      this.actionRequests.accept.acceptance.set(type, []);
    }

    this.actionRequests.accept.acceptance.get(type).push(id);
  }

  public addAcceptIntoRequest(type: CubiqTS.AcceptanceType, id: string) {
    if (!this.actionRequests.acceptInto.acceptance.has(type)) {
      this.actionRequests.acceptInto.acceptance.set(type, []);
    }

    this.actionRequests.acceptInto.acceptance.get(type).push(id);
  }

  public addXferAcceptRequest(targetDeskId: string, id: string) {
    if (!this.actionRequests.accept.xfer.has(targetDeskId)) {
      this.actionRequests.accept.xfer.set(targetDeskId, []);
    }

    this.actionRequests.accept.xfer.get(targetDeskId).push(id);
  }

  public addXferAcceptIntoRequest(targetDeskId: string, id: string) {
    if (!this.actionRequests.acceptInto.xfer.has(targetDeskId)) {
      this.actionRequests.acceptInto.xfer.set(targetDeskId, []);
    }

    this.actionRequests.acceptInto.xfer.get(targetDeskId).push(id);
  }

  public get hasAcceptIntoElegible(): boolean {
    return (
      this.count > 0 &&
      this.acceptIntoElegible === this.count &&
      (this.callCount === 0 || this.acceptIntoElegible === this.callCount)
    );
  }

  /**
   * call orders without a RateSideType
   */
  public get hasCallOnly(): boolean {
    return (
      this.callCount > 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      !this.ineligibleItem &&
      this.migrationCount === 0 &&
      this.modifyInCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasCancelOnly(): boolean {
    return (
      this.cancelCount > 0 &&
      this.callCount === 0 &&
      this.expiryCount === 0 &&
      !this.ineligibleItem &&
      this.migrationCount === 0 &&
      this.modifyInCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasExpiryOnly(): boolean {
    return (
      this.expiryCount > 0 &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      !this.ineligibleItem &&
      this.migrationCount === 0 &&
      this.modifyInCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasNoElegible(): boolean {
    return (
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      this.migrationCount === 0 &&
      this.modifyInCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasMigrationOnly(): boolean {
    return (
      this.migrationCount > 0 &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      !this.ineligibleItem &&
      this.modifyInCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasModifyInOnly(): boolean {
    return (
      this.modifyInCount > 0 &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      !this.ineligibleItem &&
      this.migrationCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasModifyOutOnly(): boolean {
    return (
      this.modifyOutCount > 0 &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      !this.ineligibleItem &&
      this.modifyInCount === 0 &&
      this.migrationCount === 0 &&
      this.newPendingCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasNewPendingOnly(): boolean {
    return (
      this.newPendingCount > 0 &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      !this.ineligibleItem &&
      this.modifyInCount === 0 &&
      this.migrationCount === 0 &&
      this.modifyOutCount === 0 &&
      this.transferCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasTransferOnly(): boolean {
    return (
      this.transferCount > 0 &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      !this.ineligibleItem &&
      this.modifyInCount === 0 &&
      this.migrationCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.pauseCount === 0 &&
      this.resumeCount === 0
    );
  }

  public get hasPauseOnly(): boolean {
    return (
      this.pauseCount > 0 &&
      !this.ineligibleItem &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      this.migrationCount === 0 &&
      this.modifyInCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.resumeCount === 0 &&
      this.transferCount === 0
    );
  }

  public get hasResumeOnly(): boolean {
    return (
      this.resumeCount > 0 &&
      !this.ineligibleItem &&
      this.callCount === 0 &&
      this.cancelCount === 0 &&
      this.expiryCount === 0 &&
      this.migrationCount === 0 &&
      this.modifyInCount === 0 &&
      this.modifyOutCount === 0 &&
      this.newPendingCount === 0 &&
      this.pauseCount === 0 &&
      this.transferCount === 0
    );
  }
}
